import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { resolveImg } from "@/components/ProductCard";
import { isFlower, flowerPriceRange } from "@/lib/sizes";
import { useAuth } from "@/context/AuthContext";

const TOP_BANNER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_78f70df4-4f18-405a-988a-1820c2ecde1a/artifacts/stjle06u_Your%20paragraph%20text%20%285%29.png";

const BANNER_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_f0142094-ad32-4092-a37e-240199f2e6dd/artifacts/fipy3dr3_Copy%20of%20Copy%20of%20Copy%20of%20Untitled%20Design.jpg";

const SMALLS_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_78f70df4-4f18-405a-988a-1820c2ecde1a/artifacts/f24fx15v_Copy%20of%20Copy%20of%20Your%20paragraph%20text%20%282%29.png";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const goShop = () => {
    if (!user) navigate("/auth?redirect=/shop");
    else navigate("/shop");
  };

  const featuredList = products.filter((p) => p.featured);
  const featured = featuredList.length ? featuredList : products;
  const track = featured.length ? [...featured, ...featured] : [];

  // Biggie Smalls carousel — always shown. Uses products tagged "Biggie Smalls",
  // otherwise falls back to the flower line so the carousel is never empty.
  const taggedSmalls = products.filter(
    (p) => (p.collection || "").trim().toLowerCase() === "biggie smalls"
  );
  const biggieSmallsItems = taggedSmalls.length
    ? taggedSmalls
    : products.filter((p) => p.category === "flower");
  const biggieSmallsShopHref = taggedSmalls.length
    ? "/shop?collection=Biggie%20Smalls"
    : "/shop?c=flower";

  // Any other named collections (excluding the two Biggie lines handled above/below)
  const collections = [];
  const byCollection = {};
  products.forEach((p) => {
    const c = (p.collection || "").trim();
    if (!c) return;
    const cl = c.toLowerCase();
    if (cl === "biggie exotics" || cl === "biggie smalls") return;
    if (!byCollection[c]) {
      byCollection[c] = [];
      collections.push(c);
    }
    byCollection[c].push(p);
  });

  return (
    <div>
      {/* Announcement bar */}
      <div
        data-testid="announcement-bar"
        className="border-b border-[#9ecbe4]/60 bg-[#9ecbe4]/25 py-3 text-center"
        style={{ boxShadow: "inset 0 -2px 0 rgba(107,169,205,0.5)" }}
      >
        <p className="text-sm font-black uppercase tracking-[0.35em] text-[#a06d34] sm:text-base">
          Shipped To Your Door
        </p>
      </div>

      {/* Hero — new ALL FLOWER $100 OZ banner (full, uncropped) */}
      <section className="bg-[#f7f3ec] pt-8 pb-14" data-testid="top-banner">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="w-full overflow-hidden border-y border-black/10"
        >
          <img
            src={TOP_BANNER_IMG}
            alt="All Flower — Ounces Start at $80"
            data-testid="top-banner-img"
            className="mx-auto block h-auto w-full max-w-[1600px] object-contain"
          />
        </motion.div>

        <div className="mx-auto mt-10 flex max-w-5xl flex-col items-center px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="hero-categories"
            className="text-3xl font-black uppercase leading-tight tracking-tight text-[#2b2520] sm:text-4xl lg:text-5xl"
          >
            Flower <span className="royal-text">·</span> Pre-Rolls{" "}
            <span className="royal-text">·</span> Concentrates{" "}
            <span className="royal-text">·</span> Blunts
          </motion.h1>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            onClick={goShop}
            data-testid="shop-collection-btn"
            className="mt-8 bg-[#c99a63] px-12 py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:bg-[#bb8850]"
          >
            Shop the Collection
          </motion.button>
        </div>
      </section>

      {/* Biggie Exotics banner — moved down one slot (previously the top hero) */}
      <section className="border-t border-black/10 bg-[#f7f3ec] pb-14" data-testid="biggie-banner">
        <Link
          to="/shop?collection=Biggie%20Exotics"
          className="block w-full overflow-hidden border-y border-black/10"
        >
          <img
            src={BANNER_IMG}
            alt="Biggie Exotics Collection"
            data-testid="biggie-banner-img"
            className="mx-auto block h-auto w-full max-w-[1600px] object-contain"
          />
        </Link>
      </section>

      {/* Featured products carousel */}
      {track.length > 0 && (
        <section className="overflow-hidden border-t border-black/10 bg-[#f7f3ec] py-12" data-testid="featured-carousel">
          <div className="mx-auto mb-6 max-w-7xl px-6">
            <p className="overline mb-1">The Collection</p>
            <h2 className="text-3xl font-black text-[#2b2520]">Featured Products</h2>
          </div>
          <div className="marquee-mask">
            <div className="marquee-track gap-6 px-6">
              {track.map((p, i) => (
                <button
                  key={`${p.id}-${i}`}
                  onClick={goShop}
                  data-testid={`carousel-item-${i}`}
                  className="group w-56 flex-shrink-0 border border-black/10 bg-white text-left transition-colors duration-300 hover:border-[#c99a63]/60"
                >
                  <div className="aspect-square overflow-hidden bg-[#f2ece1]">
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="overline mb-1">{p.category}</p>
                    <p className="truncate text-sm font-bold text-[#2b2520]">{p.name}</p>
                    <p className="mt-1 text-sm text-[#a06d34]">
                      {isFlower(p)
                        ? (() => {
                            const r = flowerPriceRange(p);
                            return r ? `from $${r.min.toFixed(2)}` : "—";
                          })()
                        : `$${(p.price || 0).toFixed(2)}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sign up & get 20% off first order */}
      <section className="border-t border-[#c99a63]/30 bg-[#c99a63]/12 py-16" data-testid="signup-promo">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="overline mb-3">Welcome Offer</p>
          <h2 className="text-3xl font-black leading-tight text-[#2b2520] sm:text-4xl">
            Sign Up &amp; Get 20% Off Your First Order
          </h2>
          <p className="mt-4 text-base text-neutral-700">
            Create your account and use code{" "}
            <span className="font-mono font-bold text-[#a06d34]">WELCOME20</span> at checkout.
          </p>
          <button
            onClick={() => (user ? goShop() : navigate("/auth"))}
            data-testid="signup-promo-btn"
            className="mt-8 bg-[#c99a63] px-10 py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:bg-[#bb8850]"
          >
            {user ? "Shop the Collection" : "Create Account"}
          </button>
        </div>
      </section>

      {/* Biggie Smalls carousel — always present, above the banner */}
      {biggieSmallsItems.length > 0 && (
        <section
          data-testid="biggie-smalls-carousel"
          className="border-t border-black/10 bg-[#f7f3ec] py-14"
        >
          {/* Biggie Smalls banner image */}
          <Link
            to={biggieSmallsShopHref}
            data-testid="biggie-smalls-banner"
            className="block w-full overflow-hidden border-y border-black/10"
          >
            <img
              src={SMALLS_IMG}
              alt="Biggie Smalls Collection"
              data-testid="biggie-smalls-banner-img"
              className="mx-auto block h-auto w-full max-w-[1600px] object-contain"
            />
          </Link>
          <div className="mx-auto mt-10 max-w-7xl px-6">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <p className="overline mb-1">Collection</p>
                <h2 className="text-3xl font-black text-[#2b2520]">Biggie Smalls</h2>
              </div>
              <Link
                to={biggieSmallsShopHref}
                data-testid="biggie-smalls-shop-all"
                className="text-xs font-semibold uppercase tracking-widest text-[#a06d34] hover:text-[#c99a63]"
              >
                Shop all →
              </Link>
            </div>
            <div className="flex gap-6 overflow-x-auto pb-4" data-testid="biggie-smalls-track">
              {biggieSmallsItems.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  data-testid={`biggie-smalls-item-${p.id}`}
                  className="group w-56 flex-shrink-0 border border-black/10 bg-white text-left transition-colors duration-300 hover:border-[#c99a63]/60"
                >
                  <div className="aspect-square overflow-hidden bg-[#f2ece1]">
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="overline mb-1">{p.category}</p>
                    <p className="truncate text-sm font-bold text-[#2b2520]">{p.name}</p>
                    <p className="mt-1 text-sm text-[#a06d34]">
                      {isFlower(p)
                        ? (() => {
                            const r = flowerPriceRange(p);
                            return r ? `from $${r.min.toFixed(2)}` : "—";
                          })()
                        : `$${(p.price || 0).toFixed(2)}`}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other named collections — shown above the banner */}
      {collections.map((name, ci) => (
        <section
          key={name}
          data-testid={`collection-${ci}`}
          className="border-t border-black/10 bg-[#f7f3ec] py-14"
        >
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <p className="overline mb-1">Collection</p>
                <h2 className="text-3xl font-black text-[#2b2520]">{name}</h2>
              </div>
              <Link
                to={`/shop?collection=${encodeURIComponent(name)}`}
                data-testid={`collection-shop-${ci}`}
                className="text-xs font-semibold uppercase tracking-widest text-[#a06d34] hover:text-[#c99a63]"
              >
                Shop all →
              </Link>
            </div>
            <div className="flex gap-6 overflow-x-auto pb-4" data-testid={`collection-track-${ci}`}>
              {byCollection[name].map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  data-testid={`collection-item-${p.id}`}
                  className="group w-56 flex-shrink-0 border border-black/10 bg-white text-left transition-colors duration-300 hover:border-[#c99a63]/60"
                >
                  <div className="aspect-square overflow-hidden bg-[#f2ece1]">
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="overline mb-1">{p.category}</p>
                    <p className="truncate text-sm font-bold text-[#2b2520]">{p.name}</p>
                    <p className="mt-1 text-sm text-[#a06d34]">
                      {isFlower(p)
                        ? (() => {
                            const r = flowerPriceRange(p);
                            return r ? `from $${r.min.toFixed(2)}` : "—";
                          })()
                        : `$${(p.price || 0).toFixed(2)}`}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Biggie collection quick links */}
      <section className="bg-[#f7f3ec] pb-16" data-testid="biggie-links">
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-center gap-4 px-6 sm:flex-row">
          <Link
            to="/shop?collection=Biggie%20Smalls"
            data-testid="shop-biggie-smalls"
            className="w-full bg-[#c99a63] px-10 py-4 text-center text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:bg-[#bb8850] sm:w-auto"
          >
            Shop Biggie Smalls
          </Link>
          <Link
            to="/shop?collection=Biggie%20Exotics"
            data-testid="shop-biggie-exotics"
            className="w-full border-2 border-[#c99a63] px-10 py-4 text-center text-sm font-semibold uppercase tracking-widest text-[#a06d34] transition-colors duration-300 hover:bg-[#c99a63]/10 sm:w-auto"
          >
            Shop Biggie Exotics
          </Link>
        </div>
      </section>
    </div>
  );
}
